import React, { useState, useContext, useEffect, useRef } from 'react';
import {
	Alert,
	Box,
	Button,
	Container,
	InputAdornment,
	Paper,
	TextField,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@mui/material';
import { Stack } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard } from '@fortawesome/free-solid-svg-icons';
import { DataContext } from '../../../../context/DataContext';
import { getRuta, simpleMessage } from '../../../../helpers/Helpers';
import { tienePermiso } from '../../../../helpers/permisos';
import { getToken } from '../../../../api/Account';
import { useNavigate, useParams } from 'react-router-dom';
import {
	addDeduccionesAsync,
	getEmployeeAntiguedadAsync,
	getNominaDetailAsync,
	getVacacionesAsync
} from '../../../../api/Employees';
import moment from 'moment/moment';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { PrintReport } from '../../../../components/PrintReport';
import { formatearFecha, formatearMonto } from '../../../../helpers/formato';
import {
	CargandoTabla,
	ConfirmDialog,
	EstadoChip,
	EstadoError,
	EstadoVacio,
	KpiCard,
	PageHeader,
	SeccionFormulario,
	useAviso
} from '../../../../components/base';
import { Dato, ERROR_OTRA_SESION, estadoDeNomina } from '../comunes';

// Una línea de un bloque del recibo: la etiqueta a la izquierda y el monto a la derecha.
const FilaMonto = ({ etiqueta, valor, total = false }) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'baseline',
			gap: 2,
			py: 1,
			...(total ? { mt: 1, borderTop: 1, borderColor: 'divider' } : {})
		}}
	>
		<Typography
			component="dt"
			variant="body2"
			sx={{
				color: total ? 'text.primary' : 'text.secondary',
				fontWeight: total ? 700 : 400
			}}
		>
			{etiqueta}
		</Typography>
		<Typography
			component="dd"
			variant="monto"
			sx={{
				m: 0,
				fontWeight: total ? 700 : 400,
				textAlign: 'right',
				whiteSpace: 'nowrap'
			}}
		>
			{formatearMonto(valor)}
		</Typography>
	</Box>
);

const NominaEmployee = () => {
	const [detalleNomina, setDetalleNomina] = useState(null);
	const [montoOtrasDeducciones, setMontoOtrasDeducciones] = useState(0);
	const [antiguedad, setAntiguedad] = useState('');
	const [isCerrado, setIsCerrado] = useState(false);

	const [dataEmployee, setDataEmployee] = useState([]);

	const [localReload, setLocalReload] = useState(false);

	// Estado local de la pantalla: esqueleto mientras carga y error con "Reintentar".
	const [cargando, setCargando] = useState(true);
	const [error, setError] = useState(false);
	const peticionActual = useRef(0);
	// Si ya hay datos en pantalla, una recarga fallida no los reemplaza por un error: se avisa.
	const hayDatos = useRef(false);
	// Último monto de otras deducciones que el servidor tiene (el que trajo la carga o el que se
	// acaba de guardar). Con él se sabe si el campo tiene cambios sin guardar.
	const [montoGuardado, setMontoGuardado] = useState(0);

	// Lo que se pide aparte (saldo de vacaciones y antigüedad) no impide ver el recibo si falla.
	const [vacacionesNoDisponible, setVacacionesNoDisponible] = useState(false);
	const [antiguedadNoDisponible, setAntiguedadNoDisponible] = useState(false);

	// "Otras deducciones": error de validación junto al campo y confirmación antes de guardar.
	const [errorMonto, setErrorMonto] = useState('');
	const [confirmando, setConfirmando] = useState(false);
	const [guardando, setGuardando] = useState(false);

	const compRef = useRef();

	let ruta = getRuta();
	const token = getToken();
	let navigate = useNavigate();
	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
	const aviso = useAviso();
	// Guardar las deducciones es AddDeducciones: el servidor exige NOMINA UPDATE.
	const puedeGuardar = tienePermiso(access, 'NOMINA UPDATE');

	const { id, ideployee } = useParams();

	const getNomina = async () => {
		const peticion = ++peticionActual.current;
		setCargando(true);
		setError(false);
		setIsLoading(true);
		try {
			const result = await getNominaDetailAsync(token, ideployee);
			if (peticion !== peticionActual.current) return;

			if (!result.statusResponse) {
				setIsLoading(false);
				setCargando(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				if (result.error === ERROR_OTRA_SESION) {
					simpleMessage(result.error, 'error');
				}
				if (hayDatos.current) {
					aviso.error('No se pudo actualizar el recibo. Recarga la página para ver su estado.');
				} else {
					setError(true);
				}
				return;
			}

			if (!result.data.result) {
				// El servidor respondió bien pero no hay recibo con ese número.
				setIsLoading(false);
				setCargando(false);
				if (hayDatos.current) {
					aviso.error('No se pudo actualizar el recibo. Recarga la página para ver su estado.');
				} else {
					setError('noexiste');
				}
				return;
			}

			hayDatos.current = true;
			setMontoGuardado(result.data.result.otrasDeducciones);
			setIsCerrado(result.data.result.nomina.estado.id === 2);
			setDetalleNomina(result.data.result);
			setMontoOtrasDeducciones(result.data.result.otrasDeducciones);
			getVacaciones(result.data.result.empleado.id);
			getAntiguedad(result.data.result.empleado.id);
			setIsLoading(false);
			setCargando(false);
		} catch (e) {
			// Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
			if (peticion !== peticionActual.current) return;
			setIsLoading(false);
			setCargando(false);
			if (hayDatos.current) {
				aviso.error('No se pudo actualizar el recibo. Recarga la página para ver su estado.');
			} else {
				setError('conexion');
			}
		}
	};

	const getAntiguedad = async (employeeId) => {
		setIsLoading(true);
		setAntiguedadNoDisponible(false);
		try {
			const result = await getEmployeeAntiguedadAsync(token, employeeId);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				if (result.error === ERROR_OTRA_SESION) {
					simpleMessage(result.error, 'error');
					return;
				}
				setAntiguedadNoDisponible(true);
				return;
			}

			setAntiguedad(result.data.result);

			setIsLoading(false);
		} catch (e) {
			setIsLoading(false);
			setAntiguedadNoDisponible(true);
		}
	};

	useEffect(() => {
		getNomina();
	}, [localReload]);

	const getVacaciones = async (employeeId) => {
		setIsLoading(true);
		setVacacionesNoDisponible(false);
		try {
			const result = await getVacacionesAsync(token, employeeId);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				if (result.error === ERROR_OTRA_SESION) {
					simpleMessage(result.error, 'error');
					return;
				}
				setVacacionesNoDisponible(true);
				return;
			}

			setDataEmployee(result.data.result);
			setIsLoading(false);
		} catch (e) {
			setIsLoading(false);
			setVacacionesNoDisponible(true);
		}
	};

	const funtionSetOtrasDeducciones = (value) => {
		if (/^\d*\.?\d*$/.test(value.toString()) || value === '') {
			setMontoOtrasDeducciones(value);
			setErrorMonto('');
			return;
		}
	};

	// Mismo envío de siempre, con el mismo payload; ahora solo ocurre tras confirmar.
	const saveDeduccionesAsync = async () => {
		setIsLoading(true);
		const data = { id: ideployee, montoOtrasDeducciones };
		try {
			const result = await addDeduccionesAsync(token, data);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				if (result.error === ERROR_OTRA_SESION) {
					simpleMessage(result.error, 'error');
					return;
				}
				aviso.error(result.error || 'No se pudieron guardar las deducciones');
				return;
			}
			setIsLoading(false);
			// El guardado salió bien: el campo ya no tiene cambios pendientes, aunque la recarga falle.
			setMontoGuardado(Number(montoOtrasDeducciones));
			aviso.exito('Deducciones guardadas');
			setLocalReload(!localReload);
		} catch (e) {
			setIsLoading(false);
			aviso.error('No se pudo conectar con el servidor. Inténtalo de nuevo.');
		}
	};

	// Primer paso: la misma regla de siempre (el campo no puede quedar vacío) y, solo si pasa,
	// se pide la confirmación con el monto. Un "." solo nunca pudo guardarse (el servidor lo rechaza).
	const pedirGuardarDeducciones = () => {
		if (montoOtrasDeducciones.length === 0) {
			setErrorMonto('Ingrese el monto de otras deducciones');
			return;
		}
		if (Number.isNaN(Number(montoOtrasDeducciones))) {
			setErrorMonto('Ingrese un monto válido, por ejemplo 150.50');
			return;
		}
		setErrorMonto('');
		setConfirmando(true);
	};

	const confirmarGuardarDeducciones = async () => {
		setGuardando(true);
		try {
			await saveDeduccionesAsync();
		} finally {
			setGuardando(false);
			setConfirmando(false);
		}
	};

	// El campo se modificó y aún no se guardó.
	const sucio =
		detalleNomina !== null &&
		(montoOtrasDeducciones === '' ||
			Number(montoOtrasDeducciones) !== Number(montoGuardado));

	// Si se sale de la pantalla con el campo modificado, un aviso lo recuerda (no bloquea la salida).
	const sucioRef = useRef(false);
	sucioRef.current = sucio && !isCerrado;
	useEffect(
		() => () => {
			if (sucioRef.current) {
				aviso.info('Las otras deducciones que escribiste no se guardaron.');
			}
		},
		[aviso]
	);

	const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page {   margin: 5mm; }
`;

	if (!detalleNomina) {
		return (
			<Container maxWidth={false} disableGutters>
				<Button
					onClick={() => navigate(`${ruta}/nominadetails/${id}`)}
					startIcon={<ArrowBackIcon />}
					sx={{ mb: 1 }}
				>
					Volver a la nómina
				</Button>
				<PageHeader
					titulo="Recibo de nómina"
					icono={<FontAwesomeIcon icon={faAddressCard} />}
				/>
				<Paper sx={{ p: { xs: 2, sm: 3 } }}>
					{cargando ? (
						<CargandoTabla filas={4} columnas={3} etiqueta="Cargando el recibo…" />
					) : error === 'noexiste' ? (
						<EstadoVacio
							titulo="No se encontró el recibo"
							descripcion="Es posible que el número sea incorrecto. Vuelve a la nómina para elegir un empleado."
						/>
					) : (
						<EstadoError
							titulo={
								error === 'conexion'
									? 'No se pudo conectar con el servidor'
									: 'No se pudo cargar el recibo'
							}
							onReintentar={getNomina}
						/>
					)}
				</Paper>
			</Container>
		);
	}

	const { empleado } = detalleNomina;
	const nombreEmpleado = String(empleado.fullName ?? '').replace(/\s+/g, ' ').trim();
	const montoAConfirmar = Number(montoOtrasDeducciones);

	// Mismo dato y misma suma de siempre para "Otras deducciones".
	const totalOtrasDeducciones =
		detalleNomina.montoPermisos +
		detalleNomina.montoReposos +
		detalleNomina.montoSubcidio +
		detalleNomina.adelanto +
		detalleNomina.otrasDeducciones;

	const textoSaldoVacaciones = () => {
		if (vacacionesNoDisponible) return 'No disponible';
		return dataEmployee.saldoVac ?? '—';
	};
	const textoAntiguedad = () => {
		if (antiguedadNoDisponible) return 'No disponible';
		return antiguedad || '—';
	};

	const valorDato = (texto) => (
		<Typography component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
			{texto}
		</Typography>
	);

	let ayudaMonto;
	let ayudaMontoProps;
	if (errorMonto) {
		ayudaMonto = errorMonto;
	} else if (isCerrado) {
		ayudaMonto = 'La nómina está cerrada: ya no se pueden cambiar las deducciones.';
	} else if (sucio) {
		ayudaMonto = 'Cambios sin guardar. Pulsa «Guardar deducciones» para aplicarlos.';
		ayudaMontoProps = { sx: { color: 'warning.main', fontWeight: 700 } };
	} else {
		ayudaMonto = 'Monto que se descuenta en esta nómina, además de lo anterior.';
	}

	return (
		<div>
			<Container maxWidth={false} disableGutters>
				<Button
					onClick={() => {
						navigate(`${ruta}/nominadetails/${id}`);
					}}
					startIcon={<ArrowBackIcon />}
					sx={{ mb: 1 }}
				>
					Volver a la nómina
				</Button>

				<PageHeader
					titulo={nombreEmpleado}
					subtitulo={`Recibo #${detalleNomina.id} · ${
						detalleNomina.nomina?.description ?? `Nómina #${id}`
					}`}
					icono={<FontAwesomeIcon icon={faAddressCard} />}
					acciones={
						<>
							<EstadoChip {...estadoDeNomina(detalleNomina.nomina?.estado)} />
							<ReactToPrint
								trigger={() => {
									return (
										<Button variant="outlined" startIcon={<PrintRoundedIcon />}>
											Imprimir recibo
										</Button>
									);
								}}
								content={() => compRef.current}
								pageStyle={pageStyle}
							/>
						</>
					}
				/>

				{!isCerrado ? (
					<Alert severity="info" sx={{ mb: 3 }}>
						Esta nómina sigue abierta: los montos pueden cambiar hasta que se cierre.
					</Alert>
				) : null}

				<Paper component="section" aria-label="Datos del recibo" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
					<Box
						component="dl"
						sx={{
							m: 0,
							display: 'grid',
							gap: 2,
							gridTemplateColumns: {
								xs: 'repeat(2, minmax(0, 1fr))',
								md: 'repeat(4, minmax(0, 1fr))'
							}
						}}
					>
						<Dato etiqueta="N.º de empleado">{valorDato(empleado.id)}</Dato>
						<Dato etiqueta="N.º de INSS">{valorDato(empleado.inssNumber || '—')}</Dato>
						<Dato etiqueta="Tipo de contrato">
							{valorDato(
								empleado.tipoContrato === 1 ? 'Contrato fijo' : 'Servicios profesionales'
							)}
						</Dato>
						<Dato etiqueta="Antigüedad">{valorDato(textoAntiguedad())}</Dato>
						<Dato etiqueta="Saldo de vacaciones">{valorDato(textoSaldoVacaciones())}</Dato>
						<Dato etiqueta="Período desde">{valorDato(formatearFecha(detalleNomina.desde))}</Dato>
						<Dato etiqueta="Período hasta">{valorDato(formatearFecha(detalleNomina.hasta))}</Dato>
						<Dato etiqueta="Salario mensual actual">
							<Typography variant="monto" component="span">
								{formatearMonto(empleado.salario)}
							</Typography>
						</Dato>
					</Box>
				</Paper>

				<Box
					sx={{
						display: 'grid',
						gap: 2,
						gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
						alignItems: 'start'
					}}
				>
					<SeccionFormulario titulo="Asignaciones">
						<Box component="dl" sx={{ m: 0, mt: 1 }}>
							<FilaMonto etiqueta="Salario ordinario" valor={detalleNomina.salario} />
							<FilaMonto
								etiqueta="Total de asignaciones"
								valor={detalleNomina.salario}
								total
							/>
						</Box>
					</SeccionFormulario>

					<SeccionFormulario titulo="Deducciones de ley">
						<Box component="dl" sx={{ m: 0, mt: 1 }}>
							<FilaMonto etiqueta="Aporte INSS" valor={detalleNomina.aporteINSS} />
							<FilaMonto
								etiqueta="Total de deducciones de ley"
								valor={detalleNomina.aporteINSS}
								total
							/>
						</Box>
					</SeccionFormulario>

					<SeccionFormulario titulo="Otras deducciones">
						<Box component="dl" sx={{ m: 0, mt: 1 }}>
							<FilaMonto etiqueta="Ausencia" valor={detalleNomina.montoPermisos} />
							<FilaMonto etiqueta="Reposo" valor={detalleNomina.montoReposos} />
							<FilaMonto etiqueta="Subsidio" valor={detalleNomina.montoSubcidio} />
							<FilaMonto etiqueta="Adelanto de salario" valor={detalleNomina.adelanto} />
						</Box>

						<TextField
							fullWidth
							size="small"
							disabled={isCerrado || !puedeGuardar}
							onChange={(e) => funtionSetOtrasDeducciones(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !isCerrado && puedeGuardar) {
									e.preventDefault();
									pedirGuardarDeducciones();
								}
							}}
							label="Otras deducciones"
							value={montoOtrasDeducciones}
							error={Boolean(errorMonto)}
							helperText={ayudaMonto}
							FormHelperTextProps={ayudaMontoProps}
							inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
							InputProps={{
								startAdornment: <InputAdornment position="start">C$</InputAdornment>
							}}
							sx={{ mt: 2 }}
						/>
						{puedeGuardar ? (
							<Button
								variant="contained"
								startIcon={<SaveOutlinedIcon />}
								disabled={isCerrado || guardando}
								onClick={pedirGuardarDeducciones}
								sx={{ mt: 1.5 }}
							>
								Guardar deducciones
							</Button>
						) : null}

						<Box component="dl" sx={{ m: 0, mt: 2 }}>
							<FilaMonto
								etiqueta="Total de otras deducciones"
								valor={totalOtrasDeducciones}
								total
							/>
						</Box>
					</SeccionFormulario>
				</Box>

				<Box sx={{ mt: 3, maxWidth: { md: 420 } }}>
					<KpiCard
						titulo="Neto a recibir"
						valor={detalleNomina.totalPercibir}
						formato="monto"
						icono={<AccountBalanceWalletOutlinedIcon />}
						tono="success"
					/>
				</Box>

				<ConfirmDialog
					abierto={confirmando}
					cargando={guardando}
					titulo="Guardar otras deducciones"
					mensaje={
						<>
							<Typography component="p" sx={{ mb: 1 }}>
								{`Se registrarán ${formatearMonto(
									montoAConfirmar
								)} como otras deducciones de ${nombreEmpleado} en esta nómina.`}
							</Typography>
							<Typography component="p" sx={{ color: 'text.secondary' }}>
								El neto a recibir se recalcula con este monto.
							</Typography>
						</>
					}
					textoConfirmar="Guardar deducciones"
					onConfirmar={confirmarGuardarDeducciones}
					onCancelar={() => setConfirmando(false)}
				/>
			</Container>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport ref={compRef} titulo={'RECIBO DE PAGO'}>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />

						<Stack spacing={1} direction="row" justifyContent="space-between">
							<Stack spacing={1}>
								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left' }}
										fontSize={10}
									>
										#EMPLEADO:
									</Typography>

									<Typography style={{ textAlign: 'left' }} fontSize={10}>
										{detalleNomina.empleado.id}
									</Typography>

									<Typography
										style={{ textAlign: 'left' }}
										color={'#2196f3'}
										fontSize={10}
									>
										/
									</Typography>

									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left' }}
										fontSize={10}
									>
										TIPO NOMINA :
									</Typography>

									<Typography style={{ textAlign: 'left' }} fontSize={10}>
										{detalleNomina.empleado.tipoContrato === 1 ? 'CF' : 'CT'}
									</Typography>
								</Stack>

								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left' }}
										fontSize={10}
									>
										NOMBRE Y APELLIDO:
									</Typography>

									<Typography style={{ textAlign: 'left' }} fontSize={10}>
										{detalleNomina.empleado.inssNumber} -{' '}
										{detalleNomina.empleado.fullName}
									</Typography>
								</Stack>

								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left' }}
										fontSize={10}
									>
										ANTIGUEDAD:
									</Typography>

									<Typography style={{ textAlign: 'left' }} fontSize={10}>
										{antiguedad}
									</Typography>
								</Stack>

								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left' }}
										fontSize={10}
									>
										PERIODO:
									</Typography>

									<Typography style={{ textAlign: 'left' }} fontSize={10}>
										{`DESDE ${moment(detalleNomina.desde).format(
											'L'
										)} HASTA  ${moment(detalleNomina.hasta).format('L')}`}
									</Typography>
								</Stack>
							</Stack>

							<Stack spacing={1} alignItems="flex-end">
								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left', fontSize: 10 }}
									>
										No. RECIBO:
									</Typography>
									<Typography style={{ textAlign: 'left', fontSize: 10 }}>
										{detalleNomina.id}
									</Typography>
								</Stack>

								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left', fontSize: 10 }}
									>
										TASA ORDINARIA:
									</Typography>

									<Typography style={{ textAlign: 'left', fontSize: 10 }}>
										{new Intl.NumberFormat('es-NI', {
											style: 'currency',
											currency: 'NIO'
										}).format(detalleNomina.empleado.salario)}
									</Typography>
								</Stack>

								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left', fontSize: 10 }}
									>
										FECHA PAGO:
									</Typography>

									<Typography style={{ textAlign: 'left', fontSize: 10 }}>
										{moment(detalleNomina.hasta).format('L')}
									</Typography>
								</Stack>

								<Stack spacing={1} direction="row">
									<Typography
										color={'#2196f3'}
										style={{ textAlign: 'left' }}
										fontSize={10}
									>
										SALDO VACACIONES:
									</Typography>

									<Typography style={{ textAlign: 'left' }} fontSize={10}>
										{dataEmployee.saldoVac}
									</Typography>
								</Stack>
							</Stack>
						</Stack>

						<TableContainer>
							<Table
								size="small"
								aria-label="simple table"
								sx={{ border: 1, borderColor: '#212121' }}
							>
								<TableHead>
									<TableRow>
										<TableCell
											align="center"
											style={{
												fontSize: 12,
												fontWeight: 'bold',
												color: '#2196f3'
											}}
										>
											ASIGNACIONES
										</TableCell>
										<TableCell
											align="center"
											style={{
												fontSize: 12,
												fontWeight: 'bold',
												color: '#2196f3'
											}}
										>
											DEDUCCIONES DE LEY
										</TableCell>

										<TableCell
											align="center"
											style={{
												fontSize: 12,
												fontWeight: 'bold',
												color: '#2196f3'
											}}
										>
											OTRAS DEDUCCIONES
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									<TableRow
										key={1}
										sx={{
											'&:last-child td, &:last-child th': { border: 0 }
										}}
									>
										<TableCell
											style={{
												fontSize: 11,
												color: '#212121',
												verticalAlign: 'top'
											}}
										>
											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													ORDINARIO
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.salario)}
												</Typography>
											</Stack>
										</TableCell>

										<TableCell
											style={{
												fontSize: 11,
												color: '#212121',
												verticalAlign: 'top'
											}}
										>
											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													APORTE INSS
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.aporteINSS)}
												</Typography>
											</Stack>
										</TableCell>

										<TableCell
											style={{
												fontSize: 11,
												color: '#212121',
												verticalAlign: 'top'
											}}
										>
											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													AUSENCIA
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.montoPermisos)}
												</Typography>
											</Stack>

											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													REPOSO
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.montoReposos)}
												</Typography>
											</Stack>

											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													SUBCIDIO
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.montoSubcidio)}
												</Typography>
											</Stack>

											<Stack
												spacing={1}
												direction="row"
												alignItems={'center'}
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													ADELANTO DE SALARIO
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.adelanto)}
												</Typography>
											</Stack>

											<Stack
												spacing={1}
												direction="row"
												alignItems={'center'}
												justifyContent="space-between"
											>
												<Typography
													color={'#2196f3'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													OTRAS DEDUCCIONES
												</Typography>

												<Typography
													style={{ textAlign: 'left', fontSize: 11 }}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(montoOtrasDeducciones)}
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>

									<TableRow
										key={2}
										sx={{
											'&:last-child td, &:last-child th': { border: 0 }
										}}
									>
										<TableCell
											style={{
												fontSize: 11,
												color: '#212121',
												verticalAlign: 'top'
											}}
										>
											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#00a152'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													TOTAL
												</Typography>

												<Typography
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.salario)}
												</Typography>
											</Stack>
										</TableCell>

										<TableCell
											style={{
												fontSize: 11,
												color: '#212121',
												verticalAlign: 'top'
											}}
										>
											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#00a152'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													TOTAL
												</Typography>

												<Typography
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(detalleNomina.aporteINSS)}
												</Typography>
											</Stack>
										</TableCell>

										<TableCell
											style={{
												fontSize: 11,
												color: '#212121',
												verticalAlign: 'top'
											}}
										>
											<Stack
												spacing={1}
												direction="row"
												justifyContent="space-between"
											>
												<Typography
													color={'#00a152'}
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													TOTAL
												</Typography>

												<Typography
													style={{
														textAlign: 'left',
														fontWeight: 'bold',
														fontSize: 11
													}}
												>
													{new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(
														detalleNomina.montoPermisos +
															detalleNomina.montoReposos +
															detalleNomina.montoSubcidio +
															detalleNomina.adelanto +
															detalleNomina.otrasDeducciones
													)}
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>

						<Stack
							spacing={1}
							direction="row"
							justifyContent="space-around"
							marginTop={5}
						>
							<Stack spacing={1} direction="row">
								<Typography
									color={'#00a152'}
									style={{
										textAlign: 'left',
										fontWeight: 'bold',
										fontSize: 11
									}}
								>
									TOTAL A RECIBIR
								</Typography>

								<Typography
									style={{
										textAlign: 'left',
										fontWeight: 'bold',
										fontSize: 11
									}}
								>
									{new Intl.NumberFormat('es-NI', {
										style: 'currency',
										currency: 'NIO'
									}).format(detalleNomina.totalPercibir)}
								</Typography>
							</Stack>

							<Stack spacing={1} direction="row">
								<Typography
									style={{
										textAlign: 'left',
										fontWeight: 'bold',
										fontSize: 10,
										textDecoration: 'overline'
									}}
								>
									RECIBI CONFORME A LA FECHA INDICADA
								</Typography>
							</Stack>
						</Stack>
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default NominaEmployee;
